import { type RefObject, useEffect, useRef } from 'react';

interface UseIsInViewportProps {
	onInViewport: () => void;
	ref: RefObject<HTMLElement>;
	rootMargin?: string;
}

export function useInViewport({
	ref,
	rootMargin = '0px 0px 200px 0px',
	onInViewport,
}: UseIsInViewportProps): void {
	// Use a ref instead of state to track viewport visibility.
	// This avoids hydration mismatches caused by IntersectionObserver firing immediately
	// after mount and calling a useState setter, which triggers a re-render.
	// The ref is exposed via a getter for consumers that only need the current value.
	const isInViewportRef = useRef(false);

	// Keep the latest callback in a ref so a new callback identity doesn't rebuild the observer.
	// A new IntersectionObserver always reports the element's current state straight away, so
	// rebuilding it on every callback change re-fired "viewed" analytics whenever nav data changed.
	const onInViewportRef = useRef(onInViewport);
	useEffect(() => {
		onInViewportRef.current = onInViewport;
	}, [onInViewport]);

	useEffect(() => {
		if (!ref.current) {
			isInViewportRef.current = false;
			return;
		}

		const observer = new IntersectionObserver(
			([entry]: IntersectionObserverEntry[]) => {
				const wasInViewport = isInViewportRef.current;
				isInViewportRef.current = entry.isIntersecting;
				// Only fire on a genuine out -> in transition, so re-reporting the current state can't
				// re-fire. Leaving and re-entering the viewport (e.g. collapse then expand) still fires.
				// Call the callback directly in the observer to avoid a chain-of-computation
				// anti-pattern (effect → state/ref → effect).
				if (entry.isIntersecting && !wasInViewport) {
					onInViewportRef.current?.();
				}
			},
			{ rootMargin },
		);

		observer.observe(ref.current);

		return () => observer.disconnect();
	}, [rootMargin, ref]);
}
